import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { Users, Search, Upload, CheckCircle2, X, Edit3, Download, FileText, Wallet, PauseCircle, PlayCircle, UserMinus } from "lucide-react";
import { toast } from "sonner";

/**
 * Master Student List — the school's authoritative student registry.
 * Admins can:
 *  - Filter by grade, section, or free-text search
 *  - Multi-select students (including "select all in Grade X" shortcut)
 *  - Bulk-assign selected students to any Subscription plan
 *  - Bulk upload students + parents via the downloadable CSV template
 *  - Inline-edit each row (name, student ID, grade, section, RFID) and assign a meal plan
 */
export default function MasterStudentList() {
  const [students, setStudents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const [gradeFilter, setGradeFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());

  const [assignPlanId, setAssignPlanId] = useState("");
  const [assigning, setAssigning] = useState(false);

  const [editing, setEditing] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  const [editPlans, setEditPlans] = useState([]); // currently assigned plans (StudentPlan rows)
  const [editPlansLoading, setEditPlansLoading] = useState(false);
  // Only subscription-type, non-expired, non-cancelled plans are shown in the modal.
  const activeSubscriptions = useMemo(
    () => editPlans.filter(p =>
      p.plan_type !== "daily"
      && p.live_status !== "expired"
      && p.live_status !== "cancelled"
    ),
    [editPlans]
  );

  const [uploading, setUploading] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/users"),
      api.get("/meal-plans/by-family"),
    ]).then(([usersR, plansR]) => {
      const all = Array.isArray(usersR.data) ? usersR.data : [];
      setStudents(all.filter(u => u.role === "student"));
      setPlans(plansR.data?.subscription || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const grades = useMemo(() => {
    const s = new Set(students.map(x => x.grade).filter(Boolean));
    return Array.from(s).sort();
  }, [students]);
  const sections = useMemo(() => {
    const s = new Set(students.map(x => x.section).filter(Boolean));
    return Array.from(s).sort();
  }, [students]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return students.filter(s => {
      if (gradeFilter && s.grade !== gradeFilter) return false;
      if (sectionFilter && s.section !== sectionFilter) return false;
      if (q && !((s.name || "").toLowerCase().includes(q)
        || (s.student_id || "").toLowerCase().includes(q)
        || (s.email || "").toLowerCase().includes(q)
        || (s.rfid_card_id || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [students, gradeFilter, sectionFilter, query]);

  const allVisibleSelected = filtered.length > 0 && filtered.every(s => selected.has(s.id));

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      const next = new Set(selected);
      filtered.forEach(s => next.delete(s.id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      filtered.forEach(s => next.add(s.id));
      setSelected(next);
    }
  };
  const clearSelection = () => setSelected(new Set());

  const doBulkAssign = async () => {
    if (!assignPlanId || selected.size === 0) return;
    setAssigning(true);
    try {
      const { data } = await api.post("/meal-plans/bulk-assign-by-ids", {
        plan_id: assignPlanId,
        student_ids: Array.from(selected),
      });
      const skipped = data.skipped || 0;
      toast.success(`Assigned ${data.assigned} student${data.assigned !== 1 ? "s" : ""}${skipped ? ` · ${skipped} skipped (conflicts)` : ""}`);
      setAssignPlanId(""); clearSelection(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk assign failed");
    } finally { setAssigning(false); }
  };

  const startEdit = (s) => {
    setEditing({
      id: s.id,
      name: s.name || "",
      student_id: s.student_id || "",
      rfid_card_id: s.rfid_card_id || "",
      grade: s.grade || "",
      section: s.section || "",
      phone: s.phone || "",
    });
    setEditPlans([]);
    setTopupAmount("");
    loadStudentPlans(s.id);
  };

  const loadStudentPlans = (studentId) => {
    setEditPlansLoading(true);
    api.get(`/meal-plans/student/${studentId}`)
      .then(r => setEditPlans(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEditPlans([]))
      .finally(() => setEditPlansLoading(false));
  };

  const holdStudentPlan = async (sp) => {
    try {
      await api.patch(`/meal-plans/student-plan/${sp.student_plan_id}/pause`);
      toast.success("Plan held");
      loadStudentPlans(editing.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hold failed");
    }
  };
  const resumeStudentPlan = async (sp) => {
    try {
      await api.patch(`/meal-plans/student-plan/${sp.student_plan_id}/resume`);
      toast.success("Plan resumed");
      loadStudentPlans(editing.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resume failed");
    }
  };
  const unassignStudentPlan = async (sp) => {
    if (!window.confirm(`Unassign ${editing?.name} from "${sp.plan_name}"? Past consumption is preserved.`)) return;
    try {
      await api.delete(`/meal-plans/student-plan/${sp.student_plan_id}`);
      toast.success("Unassigned");
      loadStudentPlans(editing.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unassign failed");
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditSaving(true);
    try {
      await api.patch(`/admin/users/${editing.id}`, {
        name: editing.name,
        student_id: editing.student_id,
        rfid_card_id: editing.rfid_card_id,
        grade: editing.grade,
        section: editing.section,
        phone: editing.phone,
      });
      toast.success("Student updated");
      if (topupAmount && Number(topupAmount) > 0) {
        try {
          await api.post("/meal-plans/admin-topup", {
            student_id: editing.id,
            amount: Number(topupAmount),
          });
          toast.success(`Wallet credited ₹${Number(topupAmount)}`);
        } catch (e3) {
          toast.error(e3?.response?.data?.detail || "Top-up failed");
        }
      }
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setEditSaving(false); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/admin/students/bulk-template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "grubpe-students-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) { toast.error("CSV needs header + data rows"); setUploading(false); return; }
      const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/[^a-z0-9_]/g, ""));
      const rows = lines.slice(1).map(line => {
        // Naive split that respects quoted fields
        const out = []; let cur = ""; let inQ = false;
        for (const ch of line) {
          if (ch === '"') { inQ = !inQ; continue; }
          if (ch === "," && !inQ) { out.push(cur); cur = ""; continue; }
          cur += ch;
        }
        out.push(cur);
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (out[i] || "").trim(); });
        return obj;
      });
      const { data } = await api.post("/admin/students/bulk-create", { rows });
      const baseMsg = `Imported: ${data.students_created} students, ${data.parents_created} parents, ${data.students_updated} updated, ${data.errors} errors`;
      if (data.parents_created > 0) {
        toast.success(baseMsg, {
          description: `New parents can sign in with default password "${data.default_password}" — share via Forgot password reset.`,
          duration: 12000,
        });
      } else {
        toast.success(baseMsg);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Students</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2 flex items-center gap-3">
              <Users size={32} className="text-brand" /> Master Student List
            </h1>
            <p className="text-sm text-ink-500 mt-1">Every student in the school — filter, multi-select, edit RFID/profile, and bulk-assign meal plans.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="bulk-template-btn" onClick={downloadTemplate}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cream-200 text-ink-700 font-semibold text-sm hover:bg-cream-300">
              <FileText size={14} /> Download CSV template
            </button>
            <label className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ink text-white font-semibold text-sm hover:bg-ink-700 cursor-pointer">
              <Upload size={14} /> {uploading ? "Uploading…" : "Bulk upload"}
              <input data-testid="bulk-upload-input" type="file" accept=".csv" className="hidden" onChange={handleBulkUpload} disabled={uploading} />
            </label>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-border p-4 mb-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Grade</label>
              <select data-testid="master-grade" value={gradeFilter} onChange={e => setGradeFilter(e.target.value)}
                className="block w-full rounded-xl border border-border px-3 py-2 text-sm bg-white focus:border-brand focus:outline-none">
                <option value="">All grades</option>
                {grades.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Section</label>
              <select data-testid="master-section" value={sectionFilter} onChange={e => setSectionFilter(e.target.value)}
                className="block w-full rounded-xl border border-border px-3 py-2 text-sm bg-white focus:border-brand focus:outline-none">
                <option value="">All sections</option>
                {sections.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Search</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                <input data-testid="master-search" type="text" placeholder="Name, student ID, email, RFID…"
                  value={query} onChange={e => setQuery(e.target.value)}
                  className="block w-full rounded-xl border border-border pl-9 pr-3 py-2 text-sm focus:border-brand focus:outline-none" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">
              {filtered.length} student{filtered.length !== 1 ? "s" : ""} · <span className="text-emerald-700">{selected.size} selected</span>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="toggle-all-visible" onClick={toggleAllVisible}
                className="text-[11px] font-bold uppercase px-3 py-1 rounded-full bg-cream-200 text-ink-700 hover:bg-cream-300">
                {allVisibleSelected ? "Deselect all visible" : `Select all visible (${filtered.length})`}
              </button>
              {selected.size > 0 && (
                <button data-testid="clear-selection" onClick={clearSelection}
                  className="text-[11px] font-bold uppercase px-3 py-1 rounded-full bg-cream-200 text-ink-700 hover:bg-cream-300">
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead className="bg-cream-200 sticky top-0">
                <tr>
                  <th className="w-10 p-3"></th>
                  <th className="text-left p-3 text-xs uppercase font-bold">Student</th>
                  <th className="text-left p-3 text-xs uppercase font-bold">Student ID</th>
                  <th className="text-left p-3 text-xs uppercase font-bold">Grade</th>
                  <th className="text-left p-3 text-xs uppercase font-bold">Section</th>
                  <th className="text-left p-3 text-xs uppercase font-bold">RFID</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="p-10 text-center text-ink-500">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={7} className="p-10 text-center text-ink-500">No students match the current filters.</td></tr>
                )}
                {!loading && filtered.map((s, i) => {
                  const checked = selected.has(s.id);
                  return (
                    <tr key={s.id} className={`border-t border-border hover:bg-cream-100 ${checked ? "bg-emerald-50/50" : ""}`}
                      data-testid={`master-row-${i}`}>
                      <td className="p-3 cursor-pointer" onClick={() => toggleOne(s.id)}>
                        <input type="checkbox" checked={checked} onChange={() => toggleOne(s.id)} onClick={e => e.stopPropagation()}
                          data-testid={`master-check-${s.id}`} />
                      </td>
                      <td className="p-3 font-semibold cursor-pointer" onClick={() => toggleOne(s.id)}>{s.name}<div className="text-[10px] text-ink-500">{s.email}</div></td>
                      <td className="p-3 font-mono text-xs text-ink-700">{s.student_id || "—"}</td>
                      <td className="p-3">{s.grade || "—"}</td>
                      <td className="p-3 text-ink-500">{s.section || "—"}</td>
                      <td className="p-3 font-mono text-xs text-ink-500">{s.rfid_card_id || "—"}</td>
                      <td className="p-3 text-right">
                        <button data-testid={`edit-student-${s.id}`} onClick={() => startEdit(s)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-ink text-white text-xs font-semibold hover:bg-ink-700">
                          <Edit3 size={11} /> Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-floating flex flex-col max-h-[95vh] sm:max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <h3 className="font-heading text-xl font-extrabold text-ink">Edit student</h3>
              <button onClick={() => setEditing(null)} className="p-1 rounded-full hover:bg-cream-200"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Name</label>
                <input data-testid="edit-name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}
                  className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Student ID</label>
                  <input data-testid="edit-student-id" value={editing.student_id} onChange={e => setEditing({ ...editing, student_id: e.target.value })}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm font-mono" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Phone</label>
                  <input data-testid="edit-phone" value={editing.phone} onChange={e => setEditing({ ...editing, phone: e.target.value })}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Grade</label>
                  <input data-testid="edit-grade" value={editing.grade} onChange={e => setEditing({ ...editing, grade: e.target.value })}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Section</label>
                  <input data-testid="edit-section" value={editing.section} onChange={e => setEditing({ ...editing, section: e.target.value })}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">RFID Card ID</label>
                <input data-testid="edit-rfid" value={editing.rfid_card_id}
                  onChange={e => setEditing({ ...editing, rfid_card_id: e.target.value })}
                  placeholder="Scan or type the card number"
                  className="block w-full rounded-xl border border-border px-3 py-2 text-sm font-mono" />
              </div>
              <div className="pt-3 border-t border-border">
                <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500 mb-1 block">
                  Active subscription
                </label>
                {editPlansLoading ? (
                  <div className="text-xs text-ink-500 py-2">Loading…</div>
                ) : activeSubscriptions.length === 0 ? (
                  <div className="text-xs text-ink-500 italic bg-cream-100 rounded-lg p-2.5">
                    No active subscription. Assign one from the Meal plans page.
                  </div>
                ) : (
                  <ul className="space-y-2" data-testid="edit-current-plans">
                    {activeSubscriptions.map((sp, i) => {
                      const status = sp.live_status || "active";
                      const isPaused = status === "paused";
                      return (
                        <li key={sp.student_plan_id} data-testid={`edit-current-plan-${i}`}
                          className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 ${
                            isPaused ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"
                          }`}>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-bold text-ink truncate flex items-center gap-1.5">
                              {sp.plan_name}
                              <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                                status === "paused" ? "bg-amber-200 text-amber-800" : "bg-emerald-200 text-emerald-800"
                              }`}>{status}</span>
                            </div>
                            <div className="text-[10px] text-ink-500">
                              {sp.start_date || "—"} → {sp.end_date || "—"}
                              {sp.term ? ` · ${sp.term}` : ""}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {isPaused ? (
                              <button data-testid={`edit-resume-${i}`} title="Resume"
                                onClick={() => resumeStudentPlan(sp)}
                                className="p-1.5 rounded-full bg-emerald-200 text-emerald-800 hover:bg-emerald-300">
                                <PlayCircle size={12} />
                              </button>
                            ) : (
                              <button data-testid={`edit-hold-${i}`} title="Hold (pause)"
                                onClick={() => holdStudentPlan(sp)}
                                className="p-1.5 rounded-full bg-amber-200 text-amber-800 hover:bg-amber-300">
                                <PauseCircle size={12} />
                              </button>
                            )}
                            <button data-testid={`edit-unassign-${i}`} title="Unassign"
                              onClick={() => unassignStudentPlan(sp)}
                              className="p-1.5 rounded-full bg-rose-200 text-rose-800 hover:bg-rose-300">
                              <UserMinus size={12} />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500 flex items-center gap-1">
                  <Wallet size={11} /> Top up wallet (optional, ₹)
                </label>
                <input data-testid="edit-topup" type="number" min="0" value={topupAmount}
                  onChange={e => setTopupAmount(e.target.value)} placeholder="0"
                  className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="px-6 py-3 border-t border-border shrink-0 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
              <button data-testid="save-edit-student" onClick={saveEdit} disabled={editSaving}
                className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 disabled:opacity-40">
                {editSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating action bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-ink text-white rounded-2xl shadow-floating px-5 py-3 flex items-center gap-3"
          data-testid="bulk-action-bar">
          <CheckCircle2 size={18} className="text-emerald-400" />
          <span className="text-sm font-bold">{selected.size} selected</span>
          <span className="text-cream-300">→</span>
          <select data-testid="bulk-assign-plan-select" value={assignPlanId} onChange={e => setAssignPlanId(e.target.value)}
            className="rounded-xl bg-ink-700 border border-ink-500 text-white text-sm px-3 py-1.5 max-w-[260px]">
            <option value="">Pick subscription plan…</option>
            {plans.filter(p => !p.archived).map(p => (
              <option key={p.id} value={p.id}>{p.name}{p.term ? ` · ${p.term}` : ""}</option>
            ))}
          </select>
          <button data-testid="bulk-assign-apply" onClick={doBulkAssign} disabled={assigning || !assignPlanId}
            className="px-4 py-1.5 rounded-full bg-brand text-white font-semibold text-sm hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed">
            {assigning ? `Assigning ${selected.size}…` : "Assign"}
          </button>
          <button onClick={clearSelection} data-testid="bulk-action-close" className="p-1 rounded-full hover:bg-ink-700">
            <X size={14} />
          </button>
        </div>
      )}
    </AppShell>
  );
}
